<script setup lang="ts">
import {
  usePermissionStore,
  type PermissionConfig
} from "@/stores/permission.store";
import PopoverComponent from "../PopoverComponent/PopoverComponent.vue";
import { computed, useTemplateRef } from "vue";
import IconComponent from "../IconComponent/IconComponent.vue";
import { IconName } from "@/constants";
import ButtonComponent from "../ButtonComponent/ButtonComponent.vue";
import { useTimeoutFn } from "@vueuse/core";

export interface TaiPermissionGateComponentProps {
  config?: PermissionConfig | Array<PermissionConfig>;
  closeAfter?: number;
}

const { config, closeAfter = 1000 } =
  defineProps<TaiPermissionGateComponentProps>();
const permissionGateRef = useTemplateRef("permission-gate");
const { hasPermission } = usePermissionStore();
const { start } = useTimeoutFn(() => {
  hide();
}, closeAfter);

const toggle = (event: Event) => {
  permissionGateRef.value?.popoverRef?.toggle(event);
};

const show = (event: Event, autohide = true) => {
  permissionGateRef.value?.popoverRef?.show(event);
  if (autohide) start();
};

const hide = () => {
  permissionGateRef.value?.popoverRef?.hide();
};

const result = computed(() => {
  // assume no config means no permission required
  if (!config) return { allowed: true };
  if (Array.isArray(config)) {
    const results = config.map((item) => hasPermission(item));
    return {
      allowed: results.every((item) => item.allowed),
      // display first failed reason in case of multiple failures
      denyReason: results.find((item) => item.denyReason)?.denyReason,
      denyCategory: results.find((item) => item.denyCategory)?.denyCategory
    };
  }
  return hasPermission(config);
});

defineExpose({
  toggle,
  show,
  hide,
  result
});
</script>

<template>
  <PopoverComponent ref="permission-gate" auto-z-index>
    <div>
      <div
        v-if="
          ['GROUP', 'EXPIRED', 'INVALID'].includes(
            result.denyCategory as string
          )
        "
        class="flex items-center gap-2 text-red-500 text-body-md"
      >
        <span>
          <IconComponent :name="IconName.AccessDenied" />
        </span>
        <p>
          {{ result.denyReason }}
        </p>
      </div>
      <div
        v-if="
          ['LIMIT', 'INSUFFICIENT_CREDITS'].includes(
            result.denyCategory as string
          )
        "
        class="flex flex-col gap-2"
      >
        <div class="flex items-center gap-2 text-red-500 text-body-md">
          <span>
            <IconComponent :name="IconName.AccessDenied" />
          </span>
          <p>
            {{ result.denyReason }}
          </p>
        </div>
        <ButtonComponent
          label="Upgrade your plan"
          variant="primary"
          outlined
          as="a"
          href="/billing"
        />
      </div>
    </div>
  </PopoverComponent>
</template>
